import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination"
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";


export interface CursorPaginatorProps {
    /** Current page cursor (null for first page) */
    current: string | null;
    /** Next page cursor (null if no more pages) */
    next: string | null;
    /** Query string for pagination links (url params without page cursors) */
    search_params: string;
    /** Optional callback for client-side navigation. When provided, renders buttons instead of anchors. */
    onNavigate?: (nextCursor: string | null, prevCursor: string | null) => void;
}


export const Paginator:React.FC<{pages:number,search_params:string,currentPage:number, isMobile?:boolean}> = ({pages,search_params,currentPage, isMobile}) => {
    const middlePages = (isMobile)?2:4;
    const params = new URLSearchParams(search_params);
    params.delete('page')
    const currentParams = (params.toString()!== "")?`${params.toString()}&`:"";

    return(
        <Pagination>
            <PaginationContent>
                {
                currentPage > 1 && (
                    <PaginationItem key="prev">
                    <PaginationPrevious href={`?${currentParams}page=${currentPage - 1}`} />
                    </PaginationItem>
                )
                }
                {
                currentPage >= 4 && (
                    <PaginationItem key={1}>
                    <PaginationLink href={`?${currentParams}page=1`} isActive={currentPage === 1}>{1}</PaginationLink>
                    </PaginationItem>
                )
                }
                {
                currentPage >= 4 && (
                    <PaginationItem key="start-ellipsis">
                    <PaginationEllipsis />
                    </PaginationItem>
                )
                }
                {
                [...Array(middlePages)].map((_, index) => {
                    const startPage = Math.max(1, Math.min(currentPage - 1, pages - 3));
                    const page = startPage + index;
                    return (
                    page <= pages && (
                        <PaginationItem key={page}>
                        <PaginationLink href={`?${currentParams}page=${page}`} isActive={currentPage === page}>{page}</PaginationLink>
                        </PaginationItem>
                    )
                    );
                })
                }
                {
                pages > 5 && currentPage < pages - 2 && (
                    <PaginationItem key="end-ellipsis">
                    <PaginationEllipsis />
                    </PaginationItem>
                )
                }
                {
                pages > 4 && currentPage < pages-2 &&  (
                    <PaginationItem key={pages}>
                    <PaginationLink href={`?${currentParams}page=${pages}`} isActive={currentPage === pages}>{pages}</PaginationLink>
                    </PaginationItem>
                )
                }
                {
                currentPage < pages && (
                    <PaginationItem key="next">
                    <PaginationNext href={`?${currentParams}page=${currentPage + 1}`} />
                    </PaginationItem>
                )
                }
            </PaginationContent>
        </Pagination>
    )   
}

export const CursorPaginator:React.FC<CursorPaginatorProps> = ({current,next,search_params,onNavigate}) => {
    const params = new URLSearchParams(search_params);
    const backCursor = params.get('prevpage'); 
    
    // params.delete('page')
    // params.delete('prevpage')    
    const cleanParams = new URLSearchParams(search_params);
    cleanParams.delete('page');
    cleanParams.delete('prevpage');

    const currentParams = (cleanParams.toString()!== "")?`${cleanParams.toString()}&`:"";

    // Next Link: Go to next, carry current as history
    const nextPage = (next)?`&page=${next}`:"";
    // Always include prevpage, so we know we can go back. If current is null (Page 1), prevpage is empty.
    const historyPage = `&prevpage=${current ?? ''}`;

    // Prev Link: Go to backCursor
    // If backCursor is "", we go to start (no page param)
    const prevLink = (backCursor)?`&page=${backCursor}`:"";
    
    const showPrev = onNavigate ? current !== null : backCursor !== null;

    const handlePrevClick = () => {
        onNavigate?.(backCursor, current);
    };

    const handleNextClick = () => {
        onNavigate?.(next, current);
    };

    return (
    <Pagination className="w-full">
        <PaginationContent className="ml-auto">
            {
            (showPrev) && (
            <PaginationItem>
                {onNavigate ? (
                    <Button 
                        variant="ghost" 
                        className="w-28 ml-auto font-semibold" 
                        onClick={handlePrevClick}
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Anterior
                    </Button>
                ) : (
                    <PaginationLink className="w-28 ml-auto font-semibold" href={`?${currentParams}${prevLink}`}>
                        <ChevronLeft className="w-4 h-4 mr-2" /> Anterior
                    </PaginationLink>
                )}
            </PaginationItem>
            )
            }
            {
            (next) && (
            <PaginationItem >
                {onNavigate ? (
                    <Button 
                        variant="ghost" 
                        className="w-28 ml-auto font-semibold" 
                        onClick={handleNextClick}
                    >
                        Siguiente <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                ) : (
                    <PaginationLink className="w-28 ml-auto font-semibold" href={`?${currentParams}${nextPage}${historyPage}`}>
                        Siguiente <ChevronRight className="w-4 h-4 ml-2" />
                    </PaginationLink>
                )}
            </PaginationItem>
            )
            }
        </PaginationContent>
    </Pagination>

    );
}